/**
 * EventCloseRateCard — Smart Admin replacement R3 (2026-05-17).
 *
 * Inline card on the Event Detail Overview tab. Operator enters actual
 * attendance count; system computes verified members (distinct contacts
 * issued vouchers via this event or its CTAs) + close rate.
 *
 * Audit verdicts incorporated:
 *   - Decision 1: voucher path (computed server-side via
 *     get_event_close_rate_stats RPC). Not event_attendees.
 *   - Decision 3 (provisional): label says "Verified members
 *     (voucher issued)" so the metric definition is explicit until
 *     Heather confirms whether she means issued vs redeemed.
 *   - Decision 5: shown for all event types (no gating).
 *   - Must-fix: attendance_count=0 is valid; close rate is NULL not
 *     divide-by-zero. The RPC handles this.
 */

import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../services/supabase";
import { updateEvent } from "../../services/events.service";
import { eventKeys } from "../../hooks/useEvents";
import { useCanEditEvent } from "../../hooks/useEventPermissions";
import { useToast } from "../ui/Toast";
import { useAuth } from "../../contexts/AuthContext";
import { attendanceSaveCompletes } from "../../utils/eventCompletePermission";
import type { Event } from "../../types/event.types";
import {
  eventTracksAttendance,
  isFutureEvent,
} from "../../utils/eventTypePolicy";
import { isDateModelActive } from "../../services/eventDateModel.service";
import { useEventDayRunState } from "./EventDayRun";
import { closedDaysAttendance } from "../../utils/msrEventDay";

interface EventCloseRateCardProps {
  event: Event;
}

interface CloseRateStats {
  event_id: string;
  attendance_count: number | null;
  verified_member_count: number;
  close_rate: number | null;
  computed_at: string;
}

const EventCloseRateCard: React.FC<EventCloseRateCardProps> = ({ event }) => {
  const { canEdit: canEditEvent } = useCanEditEvent(event.id);
  // Who may enter attendance is the server's rule (can_set_event_attendance, migration 402): admin/superadmin,
  // an MSR assigned to a class / seminar / speaking engagement, or the creator / an edit permission. An MSR no
  // longer has general edit rights on an event (D13), so this cannot be derived from useCanEditEvent.
  const { data: canEnterAttendance } = useQuery({
    queryKey: ["can-set-event-attendance", event.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("can_set_event_attendance", {
        p_event_id: event.id,
      });
      return !error && data === true;
    },
    staleTime: 30_000,
  });
  // Is this event in the MSR day model? (server rule; any signed-in user may ask)
  const { data: dayModel } = useQuery({
    queryKey: ["event-date-model-active", event.id],
    queryFn: () => isDateModelActive(event.id),
    staleTime: 60_000,
  });
  const { data: runState } = useEventDayRunState(event.id, dayModel === true);
  // Attendance comes from the day closes only when a day is tracked or closed (server: _msr_attendance_from_closes,
  // migration 505). A model event whose days are all untracked (no rep, or before the tracking date) keeps manual
  // entry. Until the run state loads, stay read-only (no write that the server might refuse).
  const isDayModel =
    dayModel === true &&
    (!runState?.days ||
      runState.days.some(
        (d) => d.close !== null || d.status !== "not_tracked",
      ));
  const closedAttendance = closedDaysAttendance(runState);
  const canEdit = canEnterAttendance === true && !isDayModel;
  const { user } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const initial =
    event.attendance_count !== null && event.attendance_count !== undefined
      ? String(event.attendance_count)
      : "";
  const [editValue, setEditValue] = useState<string>(initial);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!isDirty) {
      setEditValue(
        event.attendance_count !== null && event.attendance_count !== undefined
          ? String(event.attendance_count)
          : "",
      );
    }
  }, [event.attendance_count, isDirty]);

  const { data: stats, isLoading } = useQuery<CloseRateStats | null>({
    queryKey: ["event-close-rate-stats", event.id, event.attendance_count],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_event_close_rate_stats", {
        p_event_id: event.id,
      });
      if (error || !data || data.length === 0) {
        return null;
      }
      return data[0] as CloseRateStats;
    },
    staleTime: 30_000,
  });

  // Phase 1.1 Ship 4a — new VM compute. Surfaces the new definition
  // (attendee + Zuora confirmation), the pending bucket, and the
  // legacy voucher count side-by-side per auditor B5. The legacy
  // column auto-hides past vm_cutover_date unless vm_show_legacy is
  // 'true'.
  const { data: vm } = useQuery<{
    verified_count: number;
    pending_zuora_count: number;
    legacy_voucher_count: number;
    new_definition_count: number;
  } | null>({
    queryKey: ["event-vm-stats", event.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        "compute_event_verified_members",
        { p_event_id: event.id },
      );
      if (error || !data || data.length === 0) {
        return null;
      }
      return data[0];
    },
    staleTime: 30_000,
  });

  const { data: cutoverConfig } = useQuery<{
    cutoverDate: string | null;
    showLegacy: boolean;
  }>({
    queryKey: ["vm-cutover-config"],
    queryFn: async () => {
      const { data } = await supabase
        .from("system_config")
        .select("key, value")
        .in("key", ["vm_cutover_date", "vm_show_legacy"]);
      const map = new Map(
        (data ?? []).map((r: { key: string; value: string }) => [
          r.key,
          r.value,
        ]),
      );
      return {
        cutoverDate: map.get("vm_cutover_date") ?? null,
        showLegacy: map.get("vm_show_legacy") !== "false",
      };
    },
    staleTime: 5 * 60_000,
  });

  // Auto-hide the legacy column after cutover_date unless operator
  // explicitly overrode vm_show_legacy=true (the default value).
  // After vm_cutover_date passes, frontend treats vm_show_legacy=true
  // as "operator override to keep showing", false as "hide".
  const pastCutover = cutoverConfig?.cutoverDate
    ? new Date() > new Date(cutoverConfig.cutoverDate)
    : false;
  const showLegacyColumn = !pastCutover || cutoverConfig?.showLegacy === true;

  // SA-2: entering attendance for an attendance-tracked event auto-completes it.
  // Only when it WOULDN'T be blocked by the shared status rule (not future-dated,
  // not already completed/cancelled) — so this never conflicts with the
  // future-event-complete guard or the cancellation immutability trigger.
  const shouldAutoComplete = (value: number | null): boolean =>
    !isDayModel &&
    value !== null &&
    eventTracksAttendance(event.type) &&
    event.status !== "completed" &&
    event.status !== "cancelled" &&
    !isFutureEvent(event.start_time);

  const updateMutation = useMutation({
    mutationFn: async ({
      value,
      autoComplete,
    }: {
      value: number | null;
      autoComplete: boolean;
      adminNeededToComplete?: boolean;
    }) => {
      // Completing is admin-only (one combined update, as before). Every other save goes through the
      // set_event_attendance command, the only attendance write an MSR has (migration 402).
      if (autoComplete && canEditEvent) {
        const update: Partial<Event> = {
          attendance_count: value,
          status: "completed" as Event["status"],
        };
        return updateEvent(event.id, update);
      }
      const { error } = await supabase.rpc("set_event_attendance", {
        p_event_id: event.id,
        p_attendance_count: value,
      });
      if (error) {
        throw error;
      }
      return null;
    },
    onSuccess: (_data, variables) => {
      showToast(
        "success",
        variables.autoComplete
          ? "Attendance saved — event marked completed."
          : variables.adminNeededToComplete
            ? "Attendance saved. An admin will mark the event completed."
            : "Attendance updated",
      );
      setIsDirty(false);
      // Invalidate the SAME key the Event Detail page reads under
      // (eventKeys.detail = ["events","detail",id]). The old code invalidated
      // ["events", id], which does not prefix-match the detail key, so the parent
      // never refetched and the saved attendance number never re-rendered — the
      // "it said it saved but did not show me the number" bug.
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(event.id) });
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: ["event-close-rate-stats", event.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["event-vm-stats", event.id],
      });
    },
    onError: (err) => {
      // Supabase errors are plain objects, not Error instances, so read .message directly. The server's
      // refusals are worded for the user (e.g. the attendance lock: "Attendance for this event is locked:
      // the event day has ended ... Ask an admin to make changes."), so show them rather than a generic line.
      const message =
        (err as { message?: string } | null)?.message ||
        "Failed to update attendance";
      showToast("error", message);
    },
  });

  const handleSave = () => {
    if (!isDirty || !canEdit || isDayModel) {
      return;
    }
    const trimmed = editValue.trim();
    if (trimmed === "") {
      updateMutation.mutate({ value: null, autoComplete: false });
      return;
    }
    const parsed = parseInt(trimmed, 10);
    if (isNaN(parsed) || parsed < 0) {
      showToast(
        "error",
        "Attendance must be a non-negative whole number (or blank to clear).",
      );
      return;
    }
    // Only an admin/superadmin may complete; for anyone else a combined update is refused whole by the
    // admin-only trigger and the attendance is lost, so save attendance alone (MSR plan slice 0).
    const { complete, adminNeededToComplete } = attendanceSaveCompletes(
      shouldAutoComplete(parsed),
      user?.role,
    );
    updateMutation.mutate({
      value: parsed,
      autoComplete: complete,
      adminNeededToComplete,
    });
  };

  // Phase 1.1 Ship 4a — close rate numerator switches to new
  // verified_count per auditor ("For confirmed close rate, numerator
  // should be verified_count"). Pending Zuora signups DON'T count
  // toward confirmed close rate until they confirm. Denominator stays
  // the operator-entered attendance.
  const attendance = isDayModel
    ? (closedAttendance.total ?? 0)
    : (event.attendance_count ?? 0);
  // CK18 (2026-06): gun shows / tables / networking events don't track
  // attendance, so they have no attendance field and no close rate — only
  // Verified members is shown. Class / speaking / seminar track both.
  const tracksAttendance = eventTracksAttendance(event.type);
  const newCloseRatePercent =
    vm !== null && vm !== undefined && attendance > 0
      ? `${((vm.verified_count / attendance) * 100).toFixed(1)}%`
      : "—";
  const legacyCloseRatePercent =
    stats?.close_rate !== null && stats?.close_rate !== undefined
      ? `${(Number(stats.close_rate) * 100).toFixed(1)}%`
      : "—";

  return (
    <div className="border-t border-gray-200 pt-6 mt-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-3">
        {tracksAttendance ? "Attendance & Close Rate" : "Verified Members"}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tracksAttendance && (
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Attendance
            </label>
            {isDayModel ? (
              <div data-testid="close-rate-card-day-attendance">
                <p className="text-2xl font-semibold text-gray-900">
                  {closedAttendance.total ?? "—"}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  From the closed event days ({closedAttendance.closedDays} of{" "}
                  {closedAttendance.days} closed). Each day&apos;s attendance is
                  entered when that day is closed in{" "}
                  <span className="font-medium">
                    Event days — start and close
                  </span>{" "}
                  above; an admin changes a closed day&apos;s number with
                  &quot;Correct numbers&quot; there.
                </p>
              </div>
            ) : canEdit ? (
              <input
                type="number"
                min={0}
                value={editValue}
                onChange={(e) => {
                  setEditValue(e.target.value);
                  setIsDirty(true);
                }}
                onBlur={handleSave}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                placeholder="—"
                disabled={updateMutation.isPending}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
              />
            ) : (
              <p className="text-2xl font-semibold text-gray-900">
                {event.attendance_count ?? "—"}
              </p>
            )}
            {!isDayModel && (
              <p className="mt-1 text-xs text-gray-500">
                How many people physically attended. Enter manually.
              </p>
            )}
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Verified members{" "}
            <span className="normal-case">(Zuora confirmed)</span>
          </label>
          <p className="text-2xl font-semibold text-gray-900">
            {vm === null || vm === undefined ? "…" : (vm.verified_count ?? 0)}
          </p>
          {vm !== null && vm !== undefined && vm.pending_zuora_count > 0 && (
            <p
              className="mt-1 text-xs text-amber-700"
              title="Attendee signed up via a CTA but the Zuora subscription webhook hasn't landed yet. Will reclassify automatically."
            >
              + {vm.pending_zuora_count} pending Zuora confirmation
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Unique members: attendees with a Zuora-confirmed subscription in the
            event window, plus sales a rep verified for this event.
          </p>
          {showLegacyColumn && (
            <p className="mt-2 text-xs text-gray-400">
              Legacy (voucher-issued):{" "}
              <span className="font-medium">
                {isLoading ? "…" : (stats?.verified_member_count ?? 0)}
              </span>
              {cutoverConfig?.cutoverDate && (
                <span className="ml-1">
                  {pastCutover
                    ? " (post-cutover, kept by operator)"
                    : ` (legacy until ${cutoverConfig.cutoverDate})`}
                </span>
              )}
            </p>
          )}
        </div>

        {tracksAttendance && (
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Close rate
            </label>
            <p className="text-2xl font-semibold text-gray-900">
              {newCloseRatePercent}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Verified members (Zuora confirmed) ÷ attendance. Pending Zuora
              signups don't count until confirmed.
            </p>
            {showLegacyColumn && (
              <p className="mt-2 text-xs text-gray-400">
                Legacy (voucher-based):{" "}
                <span className="font-medium">{legacyCloseRatePercent}</span>
              </p>
            )}
          </div>
        )}
      </div>
      {!tracksAttendance && (
        <p className="mt-2 text-xs text-gray-500">
          Attendance and close rate aren't tracked for this event type.
          {event.attendance_count !== null &&
            event.attendance_count !== undefined &&
            ` Previously recorded attendance: ${event.attendance_count} (no longer counted).`}
        </p>
      )}
    </div>
  );
};

export default EventCloseRateCard;
